import React, { useContext, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Checkbox, Dropdown, Header, Icon, Ref } from 'semantic-ui-react'
import { ClippedContent, DateTimeUtils, EllipsisDropdownPortal, OnHoverOnClick, Person, Table, USER_PERMISSIONS } from 'dyl-components';

import {
    KEY_CREATED, KEY_OWNER_ID, KEY_LAST_MODIFIED,
    KEY_ACCOUNT, KEY_STAGE,
    KEY_CAMPAIGN_SOURCE, KEY_CONVERT, KEY_ACTIONS,
    KEY_CREATED_BY, KEY_LAST_MODIFIED_BY, KEY_OUTCOME,
    KEY_CO_OWNER,
    KEY_LOST_REASON,
    KEY_MASTER_SECONDARY
} from 'shared/constants/MODULE_COLUMNS';

import Contact from 'shared/Contact';
import AccountLink from 'shared/AccountLink';
import ModuleLink from 'shared/ModuleLink';
import ContactConversionControls from 'shared/ContactConversionControls';
import { useDispatch } from 'react-redux';
import opportunitiesActions from 'actions/opportunities';
import HoverContent from 'shared/hoverContent';
import { BulkActionsContext } from 'shared/context/BulkActionsProvider';
import RestrictedView from 'shared/RestrictedView';

const OpportunitysRow = ({
    opportunity,
    isColumnHidden,
    setSelectedOpportunity,
    setModalRef,
    onSelectOpportunityForConversion,
}) => {
    const { contact, account, pipeline_stage_summary, pipeline_name, outcome = '', created_by, last_modified_by, owned_by, co_owned_by, close_reason, close_type, pipeline_master_source, pipeline_secondary_source
    } = opportunity;

    const dispatch = useDispatch();
    const [params] = useSearchParams();

    const refresh = () => {
        dispatch(opportunitiesActions.readOpportunities({ ...Object.fromEntries(params), limit: 25 }));
    }
    const [selectedOpportunities, setSelectedOpportunities] =
        useContext(BulkActionsContext);
        

    const isSelected = (opportunity_id) => {
        return selectedOpportunities.includes(opportunity_id);
    };

    const onToggleOpportunity = (opportunity_id) => {
        const newSelectedOpportunities = isSelected(opportunity_id)
            ? selectedOpportunities.filter((id) => opportunity_id !== id)
            : [...selectedOpportunities, opportunity_id];
        setSelectedOpportunities(newSelectedOpportunities);
    };
    const ref = useRef();
    return (
        <Table.Row>
            <Table.Cell>
                <Checkbox
                    checked={isSelected(opportunity?.contact_id)}
                    onChange={() => {
                        onToggleOpportunity(opportunity?.contact_id);
                    }}
                />
            </Table.Cell>
            {
                !isColumnHidden(KEY_CREATED) &&
                <Table.Cell>
                    <div><b>{DateTimeUtils.formatEpoch(opportunity.pipeline_created, DateTimeUtils.WORD_DATE_FORMAT)}</b></div>
                    <div>{DateTimeUtils.formatEpoch(opportunity.pipeline_created, DateTimeUtils.TIME_FORMAT)}</div>
                </Table.Cell>
            }
            {
                !isColumnHidden(KEY_CREATED_BY) &&
                <Table.Cell>
                    {created_by && (
                        <Person
                            username={created_by.username}
                            email={created_by.email}
                            isInactiveUser={!created_by.active}
                        />
                    )}
                </Table.Cell>
            }
            {
                !isColumnHidden(KEY_LAST_MODIFIED) &&
                <Table.Cell>
                    {
                        opportunity.pipeline_created !== opportunity.pipeline_activity && (
                            <React.Fragment>
                                <div><b>{opportunity.pipeline_activity && DateTimeUtils.formatEpoch(opportunity.pipeline_activity, DateTimeUtils.WORD_DATE_FORMAT)}</b></div>
                                <div>{opportunity.pipeline_activity && DateTimeUtils.formatEpoch(opportunity.pipeline_activity, DateTimeUtils.TIME_FORMAT)}</div>
                            </React.Fragment>
                        )
                    }

                </Table.Cell>
            }
            {
                !isColumnHidden(KEY_LAST_MODIFIED_BY) &&
                <Table.Cell>
                    {last_modified_by && (
                        <Person
                            username={last_modified_by.username}
                            email={last_modified_by.email}
                            isInactiveUser={!last_modified_by.active}
                        />
                    )}
                </Table.Cell>
            }
            <Table.Cell>
                <ModuleLink pipeline_id={opportunity.pipeline_id} record={opportunity}/>
            </Table.Cell>
            <Table.Cell>
                {pipeline_name}
            </Table.Cell>
            <Table.Cell>
                <Contact
                    contact={{
                        ...contact,
                        email: contact?.primary_email || {},
                        phone: contact?.primary_phone || {}
                    }}
                    name={`${contact.first_name} ${contact.last_name}`}
                    linkToContact={
                        <RestrictedView type="link" userPermission={USER_PERMISSIONS.CONTACTS_VIEW} userRestrict assignmentIds={[(opportunity?.contact?.owner_id || 0), (opportunity?.contact?.coowner_id || 0)]}>
                            <Link to={`/contact/${contact.id}`}>{`${contact.first_name} ${contact.last_name}`}</Link>
                        </RestrictedView>
                    }
                />
            </Table.Cell>
            {!isColumnHidden(KEY_ACCOUNT) && (
                <Table.Cell >
                    {account?.id &&
                        <AccountLink
                            id={account.id}
                            name={account.name}
                            type={account.type}
                            account={account}
                        />
                    }
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_STAGE) && (
                <Table.Cell>
                    <Header as='h5'>
                        {pipeline_stage_summary?.pipeline_stage_name}
                        <Header.Subheader>
                            {pipeline_stage_summary?.pipeline_category_name}
                        </Header.Subheader>
                    </Header>
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_OUTCOME) && (
                <Table.Cell>
                    {
                        outcome !== '' ?
                            <h5 className={`ModulesListTable__Outcome${outcome.split('(')[0] === 'won' ? '--won' : '--lost'}`}>
                                {outcome.charAt(0).toUpperCase() + outcome.slice(1).replace('(', ' (')}
                            </h5> : ''
                    }
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_LOST_REASON) && (
                <Table.Cell>
                    <div className='OpportunityTable__MediumFixedWidth'>
                        <ClippedContent>
                            {close_reason?.name && close_type?.name ? `${close_reason?.name} / ${close_type?.name}` : ''}
                        </ClippedContent>
                    </div>
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_MASTER_SECONDARY) && (
                <Table.Cell>
                    <div className='OpportunityTable__MediumFixedWidth'>
                        <ClippedContent>
                            {`${pipeline_master_source ? pipeline_master_source : ''}${pipeline_secondary_source ? `/${pipeline_secondary_source.name}` : ''}`}
                        </ClippedContent>
                    </div>
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_OWNER_ID) && (
                <Table.Cell className="OpportunityTable__LargeFixedWidth">
                    {owned_by ? <OnHoverOnClick
                        hoverContent={
                            <HoverContent
                                user={owned_by}
                            />
                        }
                        clickable={false}
                        trigger={owned_by ?
                            <div className='TableTruncateFixedWidth'>
                                <Person
                                    username={`${owned_by.first_name} ${owned_by.last_name}`}
                                    email={owned_by.email}
                                    isInactiveUser={!owned_by.active}
                                    popup={false}
                                    title={" "}
                                />
                            </div>
                            : undefined}
                    /> : "Unassigned"}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_CO_OWNER) && (
                <Table.Cell className="OpportunityTable__LargeFixedWidth">
                    {co_owned_by ? <OnHoverOnClick
                        hoverContent={
                            <HoverContent
                                user={co_owned_by}
                            />
                        }
                        clickable={false}
                        trigger={co_owned_by ?
                            <div className='TableTruncateFixedWidth'>
                                <Person
                                    username={`${co_owned_by.first_name} ${co_owned_by.last_name}`}
                                    email={co_owned_by.email}
                                    isInactiveUser={!co_owned_by.active}
                                    popup={false}
                                    title={" "}
                                />
                            </div>
                            : undefined}
                    /> : "Unassigned"}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_CAMPAIGN_SOURCE) && (
                <Table.Cell>

                </Table.Cell>
            )}
            {!isColumnHidden(KEY_CONVERT) && (
                <Table.Cell>
                    {outcome === '' && !(contact.id === account?.pipeline_person_id) ?
                        <ContactConversionControls
                            account_id={account?.id}
                            pipeline_id={opportunity.pipeline_id}
                            account_stage={'opportunity'}
                            contactName={`${contact.first_name} ${contact.last_name}`}
                            contact_id={contact.id}
                            linkedAccountIsACustomer={account?.customer}
                            linkedAccountName={account?.name}
                            linkedAccountPrimaryContact={account?.primary_person_id}
                            refresh={refresh}
                            isDeactivatedCustomer={account?.customer_status === 'canceled' || account?.customer_status === 'paused'}
                            location={contact.location}
                            phone={contact.primary_phone}
                            email={contact.primary_email}
                            contact={opportunity}
                            onSelectForConversion={() => {onSelectOpportunityForConversion(opportunity.pipeline_id)}}
                        />
                        : ''}
                </Table.Cell>
            )}
            {!isColumnHidden(KEY_ACTIONS) && (
                <Table.Cell>
                    {outcome === '' && (
                        <Ref innerRef={ref}>
                            <EllipsisDropdownPortal reference={ref}>
                                <RestrictedView type="disabled" userPermission={USER_PERMISSIONS.OPPORTUNITY_EDIT} userRestrict assignmentIds={[(opportunity?.contact?.owner_id || 0), (opportunity?.contact?.coowner_id || 0)]}>
                                    <Dropdown.Item onClick={() => { setSelectedOpportunity(opportunity); setModalRef(ref) }} text={<React.Fragment>
                                        <Icon name={'fas fa-sitemap'} />
                                        Sequences
                                    </React.Fragment>}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </RestrictedView>
                            </EllipsisDropdownPortal>
                        </Ref>
                    )}
                </Table.Cell>
            )}
           
        </Table.Row>
    );
}

export default OpportunitysRow;
